import { ISnippet } from '@principle-theorem/editor';
import {
  BrandCollection,
  ConversationStatus,
  DEFAULT_RECALL_PERIOD_IN_MONTHS,
  EmailMessage,
  EventType,
  IAppointmentRequest,
  IBrand,
  IBrandSettings,
  ICalendarEvent,
  ICalendarEventSchedule,
  ICandidateCalendarEvent,
  IChat,
  IContact,
  ICustomFormConfiguration,
  ILab,
  IMarketingCampaign,
  IMarketingCampaignEntry,
  IMarketingCampaignRun,
  IMedia,
  IOrganisation,
  IParticipant,
  IPatient,
  IPatientMetadataConfiguration,
  IPatientMetadataDisplay,
  IPatientPhoneNumber,
  IPractice,
  IPrescriptionItem,
  IProduct,
  IReferral,
  IReferralSourceConfiguration,
  ISchedulingAlert,
  ISchedulingEventReason,
  IStaffer,
  ISterilisationCycleType,
  ISterilisationEquipment,
  ISterilisationMachine,
  ISterilisationPack,
  ITag,
  ITeam,
  ITemplateDefinition,
  ITreatmentCategory,
  ITreatmentTemplate,
  IUser,
  MarketingCampaignCollection,
  MarketingCampaignEntryStatus,
  MarketingCampaignRunStatus,
  ReportingJob,
  ReservedCustomFormId,
  SMSConversation,
  SMSMessage,
} from '@principle-theorem/principle-core/interfaces';
import {
  CollectionReference,
  DocumentReference,
  Firestore,
  IReffable,
  ITimePeriod,
  Query,
  Timezone,
  WithRef,
  all$,
  collectionGroupQuery,
  doc,
  getDocs,
  initFirestoreModel,
  isSameRef,
  limit,
  multiFilter,
  multiMap,
  multiSortBy$,
  multiSwitchMap,
  multiUndeleted,
  nameSorter,
  orderBy,
  query$,
  reduce2DArray,
  reduceToSingleArray,
  safeCombineLatest,
  slugify,
  sortByRefArray,
  subCollection,
  timePeriodsIntersect,
  toMoment,
  toQuery,
  toTimePeriod,
  undeletedQuery,
  where,
} from '@principle-theorem/shared';
import { chunk, compact, flatten, sortBy, uniqWith } from 'lodash';
import * as moment from 'moment-timezone';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MarketingCampaign } from './marketing/campaign';
import { OrganisationCache } from './organisation/organisation-cache';

export class BrandSettings {
  static init(): IBrandSettings {
    return {
      timezone: Timezone.AustraliaSydney,
      recalls: {
        periodInMonths: DEFAULT_RECALL_PERIOD_IN_MONTHS,
        treatmentRefs: [],
      },
    };
  }
}

export class Brand {
  static init(overrides?: Partial<IBrand>): IBrand {
    const brand: IBrand = {
      name: '',
      slug: '',
      settings: BrandSettings.init(),
      defaultSchedulingEventReasonOrder: [],
      ...initFirestoreModel(),
      ...overrides,
    };
    return {
      ...brand,
      slug: slugify(brand.name.toLowerCase()),
    };
  }

  static teamCol(brand: IReffable<IBrand>): CollectionReference<ITeam> {
    return subCollection<ITeam>(brand.ref, BrandCollection.Teams);
  }

  static teams$(brand: IReffable<IBrand>): Observable<WithRef<ITeam>[]> {
    return query$(undeletedQuery(Brand.teamCol(brand)));
  }

  static contactCol(brand: IReffable<IBrand>): CollectionReference<IContact> {
    return subCollection<IContact>(brand.ref, BrandCollection.Contacts);
  }

  static contacts$(brand: IReffable<IBrand>): Observable<WithRef<IContact>[]> {
    return query$(undeletedQuery(Brand.contactCol(brand)));
  }

  static contactMembers$(
    brand: IReffable<IBrand>,
    contactMember: IReffable<IContact>
  ): Observable<WithRef<IContact>[]> {
    return query$(
      Brand.contactCol(brand),
      where('parentRef.ref', '==', contactMember.ref)
    ).pipe(multiFilter((contact) => !contact.deleted));
  }

  static referralCol(brand: IReffable<IBrand>): CollectionReference<IReferral> {
    return subCollection<IReferral>(brand.ref, BrandCollection.Referrals);
  }

  static referralSourceCol(
    brand: IReffable<IBrand>
  ): CollectionReference<IReferralSourceConfiguration> {
    return subCollection<IReferralSourceConfiguration>(
      brand.ref,
      BrandCollection.ReferralSources
    );
  }

  static referralSources$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<IReferralSourceConfiguration>[]> {
    return query$(undeletedQuery(Brand.referralSourceCol(brand))).pipe(
      multiSortBy$(nameSorter())
    );
  }

  static cancellationReasonCol(
    brand: IReffable<IBrand>
  ): CollectionReference<ISchedulingEventReason> {
    return subCollection<ISchedulingEventReason>(
      brand.ref,
      BrandCollection.CancellationReasons
    );
  }

  static cancellationReasons$(
    brandReffable: IReffable<IBrand>,
    includeDeleted: boolean = false
  ): Observable<WithRef<ISchedulingEventReason>[]> {
    const collection = Brand.cancellationReasonCol(brandReffable);
    const results$ = includeDeleted
      ? all$(collection)
      : query$(undeletedQuery(collection));
    return combineLatest([
      OrganisationCache.brands.doc$(brandReffable.ref),
      results$,
    ]).pipe(
      map(([brand, reasons]) =>
        sortByRefArray(reasons, brand.defaultSchedulingEventReasonOrder)
      )
    );
  }

  static treatmentTemplateCol(
    brand: IReffable<IBrand>
  ): CollectionReference<ITreatmentTemplate> {
    return subCollection<ITreatmentTemplate>(
      brand.ref,
      BrandCollection.TreatmentTemplates
    );
  }

  static treatmentTemplates$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<ITreatmentTemplate>[]> {
    return all$(undeletedQuery(Brand.treatmentTemplateCol(brand)));
  }

  static treatmentTemplates(
    brand: IReffable<IBrand>
  ): Promise<WithRef<ITreatmentTemplate>[]> {
    return getDocs(undeletedQuery(Brand.treatmentTemplateCol(brand)));
  }

  static treatmentCategoryCol(
    brand: IReffable<IBrand>
  ): CollectionReference<ITreatmentCategory> {
    return subCollection<ITreatmentCategory>(
      brand.ref,
      BrandCollection.TreatmentCategories
    );
  }

  static treatmentCategories$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<ITreatmentCategory>[]> {
    return all$(undeletedQuery(Brand.treatmentCategoryCol(brand))).pipe(
      map((categories) =>
        sortBy(categories, (category) => category.name.toLowerCase())
      )
    );
  }

  static labCol(brand: IReffable<IBrand>): CollectionReference<ILab> {
    return subCollection<ILab>(brand.ref, BrandCollection.Labs);
  }

  static labs$(brand: IReffable<IBrand>): Observable<WithRef<ILab>[]> {
    return query$(undeletedQuery(Brand.labCol(brand)));
  }

  static stafferCol(brand: IReffable<IBrand>): CollectionReference<IStaffer> {
    return subCollection<IStaffer>(brand.ref, BrandCollection.Staff);
  }

  static staff$(
    brand: IReffable<IBrand>,
    includeDeleted: boolean = false
  ): Observable<WithRef<IStaffer>[]> {
    const staffQuery = includeDeleted
      ? Brand.stafferCol(brand)
      : undeletedQuery(Brand.stafferCol(brand));
    return OrganisationCache.staff.all
      .query$(
        {
          brandRef: brand.ref,
          includeDeleted,
        },
        staffQuery
      )
      .pipe(map((staff) => sortBy(staff, ['user.name'])));
  }

  static sterilisationMachineCol(
    brand: IReffable<IBrand>
  ): CollectionReference<ISterilisationMachine> {
    return subCollection<ISterilisationMachine>(
      brand.ref,
      BrandCollection.SterilisationMachines
    );
  }

  static sterilisationMachines$(
    brand: IReffable<IBrand>,
    practiceRef?: DocumentReference<IPractice>
  ): Observable<WithRef<ISterilisationMachine>[]> {
    let query = undeletedQuery(Brand.sterilisationMachineCol(brand));
    if (practiceRef) {
      query = toQuery(query, where('practice.ref', '==', practiceRef));
    }
    return query$(query);
  }

  static sterilisationPacksCol(
    brand: IReffable<IBrand>
  ): CollectionReference<ISterilisationPack> {
    return subCollection<ISterilisationPack>(
      brand.ref,
      BrandCollection.SterilisationPacks
    );
  }

  static sterilisationPacks$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<ISterilisationPack>[]> {
    return query$(undeletedQuery(Brand.sterilisationPacksCol(brand)));
  }

  static sterilisationCycleTypesCol(
    brand: IReffable<IBrand>
  ): CollectionReference<ISterilisationCycleType> {
    return subCollection<ISterilisationCycleType>(
      brand.ref,
      BrandCollection.SterilisationCycleTypes
    );
  }

  static sterilisationCycleTypes$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<ISterilisationCycleType>[]> {
    return query$(undeletedQuery(Brand.sterilisationCycleTypesCol(brand)));
  }

  static sterilisationEquipmentCol(
    brand: IReffable<IBrand>
  ): CollectionReference<ISterilisationEquipment> {
    return subCollection<ISterilisationEquipment>(
      brand.ref,
      BrandCollection.SterilisationEquipment
    );
  }

  static sterilisationEquipment$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<ISterilisationEquipment>[]> {
    return query$(undeletedQuery(Brand.sterilisationEquipmentCol(brand)));
  }

  static userStaffer$(
    brand: IReffable<IBrand>,
    user: IReffable<IUser>,
    filterDeleted: boolean = true
  ): Observable<WithRef<IStaffer> | undefined> {
    return OrganisationCache.staff.all.find$(
      {
        brandRef: brand.ref,
        userRef: user.ref,
        filterDeleted,
      },
      Brand.stafferCol(brand),
      ...compact([
        where('user.ref', '==', user.ref),
        filterDeleted ? where('deleted', '!=', true) : undefined,
      ])
    );
  }

  static calendarEventCol(
    brand: IReffable<IBrand>
  ): CollectionReference<ICalendarEvent> {
    return subCollection<ICalendarEvent>(
      brand.ref,
      BrandCollection.CalendarEvents
    );
  }

  static patientCol(brand: IReffable<IBrand>): CollectionReference<IPatient> {
    return subCollection<IPatient>(brand.ref, BrandCollection.Patients);
  }

  static practiceCol(brand: IReffable<IBrand>): CollectionReference<IPractice> {
    return subCollection<IPractice>(brand.ref, BrandCollection.Practices);
  }

  static practices$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<IPractice>[]> {
    return all$(undeletedQuery(Brand.practiceCol(brand)));
  }

  static practices(brand: IReffable<IBrand>): Promise<WithRef<IPractice>[]> {
    return Firestore.getDocs(undeletedQuery(Brand.practiceCol(brand)));
  }

  static templateCol(
    brand: IReffable<IBrand>
  ): CollectionReference<ITemplateDefinition> {
    return subCollection<ITemplateDefinition>(
      brand.ref,
      BrandCollection.Templates
    );
  }

  static templates$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<ITemplateDefinition>[]> {
    return query$(undeletedQuery(Brand.templateCol(brand)));
  }

  static schedulingAlertCol(
    brand: IReffable<IBrand>
  ): CollectionReference<ISchedulingAlert> {
    return subCollection<ISchedulingAlert>(
      brand.ref,
      BrandCollection.SchedulingAlerts
    );
  }

  static schedulingAlerts$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<ISchedulingAlert>[]> {
    return all$(Brand.schedulingAlertCol(brand));
  }

  static prescriptionItemCol(
    brand: IReffable<IBrand>
  ): CollectionReference<IPrescriptionItem> {
    return subCollection<IPrescriptionItem>(
      brand.ref,
      BrandCollection.PrescriptionItems
    );
  }

  static prescriptionItems$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<IPrescriptionItem>[]> {
    return query$(undeletedQuery(Brand.prescriptionItemCol(brand)));
  }

  static productCol(brand: IReffable<IBrand>): CollectionReference<IProduct> {
    return subCollection<IProduct>(brand.ref, BrandCollection.Products);
  }

  static products$(brand: IReffable<IBrand>): Observable<WithRef<IProduct>[]> {
    return query$(undeletedQuery(Brand.productCol(brand)));
  }

  static snippetCol(brand: IReffable<IBrand>): CollectionReference<ISnippet> {
    return subCollection<ISnippet>(brand.ref, BrandCollection.Snippets);
  }

  static snippets$(brand: IReffable<IBrand>): Observable<WithRef<ISnippet>[]> {
    return all$(Brand.snippetCol(brand));
  }

  static eventTagCol(brand: IReffable<IBrand>): CollectionReference<ITag> {
    return subCollection<ITag>(brand.ref, BrandCollection.CalendarEventTags);
  }

  static eventTags$(brand: WithRef<IBrand>): Observable<WithRef<ITag>[]> {
    return all$(undeletedQuery(Brand.eventTagCol(brand)));
  }

  static calendarEventScheduleCol(
    brand: IReffable<IBrand>
  ): CollectionReference<ICalendarEventSchedule> {
    return subCollection<ICalendarEventSchedule>(
      brand.ref,
      BrandCollection.CalendarEventSchedules
    );
  }

  static calendarEventSchedules$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<ICalendarEventSchedule>[]> {
    return query$(undeletedQuery(Brand.calendarEventScheduleCol(brand)));
  }

  static appointmentTagCol(
    brand: IReffable<IBrand>
  ): CollectionReference<ITag> {
    return subCollection<ITag>(brand.ref, BrandCollection.AppointmentTags);
  }

  static appointmentTags$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<ITag>[]> {
    return OrganisationCache.appointmentTags
      .query$(
        { brandRef: brand.ref },
        undeletedQuery(Brand.appointmentTagCol(brand))
      )
      .pipe(map((tag) => sortBy(tag, 'name')));
  }

  static contactTagCol(brand: IReffable<IBrand>): CollectionReference<ITag> {
    return subCollection<ITag>(brand.ref, BrandCollection.ContactTags);
  }

  static contactTags$(brand: IReffable<IBrand>): Observable<WithRef<ITag>[]> {
    return Firestore.query$(Brand.contactTagCol(brand)).pipe(multiUndeleted());
  }

  static patientTagCol(brand: IReffable<IBrand>): CollectionReference<ITag> {
    return subCollection<ITag>(brand.ref, BrandCollection.PatientTags);
  }

  static patientTags$(brand: IReffable<IBrand>): Observable<WithRef<ITag>[]> {
    return all$(undeletedQuery(Brand.patientTagCol(brand)));
  }

  static patientNoteTagCol(
    brand: IReffable<IBrand>
  ): CollectionReference<ITag> {
    return subCollection<ITag>(brand.ref, BrandCollection.PatientNoteTags);
  }

  static patientNoteTags$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<ITag>[]> {
    return all$(undeletedQuery(Brand.patientNoteTagCol(brand)));
  }

  static marketingCampaignCol(
    brand: IReffable<IBrand>
  ): CollectionReference<IMarketingCampaign> {
    return subCollection<IMarketingCampaign>(
      brand.ref,
      BrandCollection.MarketingCampaigns
    );
  }

  static marketingCampaigns$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<IMarketingCampaign>[]> {
    return all$(Brand.marketingCampaignCol(brand)).pipe(
      multiFilter((campaign) => !campaign.deleted),
      map((campaigns) =>
        sortBy(campaigns, (campaign) => campaign.name.toLowerCase())
      )
    );
  }

  static marketingAlerts$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<IMarketingCampaignRun>[]> {
    return Brand.marketingCampaigns$(brand).pipe(
      multiSwitchMap((campaign) =>
        query$(
          MarketingCampaign.marketingCampaignRunCol(campaign),
          where('status', '==', MarketingCampaignRunStatus.PendingPayment)
        )
      ),
      reduce2DArray()
    );
  }

  static unacknowledgedPaymentAlerts$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<IMarketingCampaignEntry>[]> {
    return query$<IMarketingCampaignEntry>(
      collectionGroupQuery<IMarketingCampaignEntry>(
        MarketingCampaignCollection.MarketingCampaignEntries,
        where('brandRef', '==', brand.ref),
        where('status', '==', MarketingCampaignEntryStatus.PausedForPayment)
      )
    ).pipe(
      multiUndeleted(),
      multiFilter((entry) => !entry.billingAlertAcknowledged)
    );
  }

  static activeSchedulingAlerts$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<ISchedulingAlert>[]> {
    return query$(
      Brand.schedulingAlertCol(brand),
      where(
        'deadline',
        '<=',
        // TODO: Build with timezone - CU-251edxw
        moment().endOf('day').add(2, 'days').toDate()
      )
    ).pipe(
      map((alerts) =>
        alerts
          .filter((alert) =>
            toMoment(alert.deadline).isSameOrAfter(moment().startOf('day'))
          )
          .sort((alertA, alertB) => {
            if (!alertA.deadline || !alertB.deadline) {
              return 0;
            }
            return alertA.deadline.seconds > alertB.deadline.seconds ? 1 : -1;
          })
      )
    );
  }

  static medicalHistoryFormRef(
    brand: IReffable<IBrand>
  ): DocumentReference<ICustomFormConfiguration> {
    return doc(
      this.customFormConfigCol(brand),
      ReservedCustomFormId.MedicalHistory
    );
  }

  static medicalHistoryFormConfig$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<ICustomFormConfiguration> | undefined> {
    return Firestore.doc$(this.medicalHistoryFormRef(brand)).pipe(
      catchError(() => of(undefined))
    );
  }

  static customFormConfigCol(
    brand: IReffable<IBrand>
  ): CollectionReference<ICustomFormConfiguration> {
    return subCollection<ICustomFormConfiguration>(
      brand.ref,
      BrandCollection.CustomFormConfigurations
    );
  }

  static customFormConfigs$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<ICustomFormConfiguration>[]> {
    return all$(Brand.customFormConfigCol(brand));
  }

  static getBrandStafferFromUser(
    brand: IReffable<IBrand>,
    user: WithRef<IUser>
  ): Observable<WithRef<IStaffer> | undefined> {
    return OrganisationCache.staff.all.find$(
      {
        brandRef: brand.ref,
        userRef: user.ref,
      },
      Brand.stafferCol(brand),
      where('user.ref', '==', user.ref),
      limit(1)
    );
  }

  static getCalendarEvents$(
    brand: IReffable<IBrand>,
    range: ITimePeriod,
    participants?: DocumentReference<IStaffer | IPatient>[],
    type?: EventType,
    practiceRef?: DocumentReference<IPractice>
  ): Observable<WithRef<ICalendarEvent>[]> {
    const inRangeEvents$ = Brand.eventQueryV2$(
      brand,
      range,
      'event.from',
      participants,
      type,
      practiceRef
    );

    const multiDayEventTypes = [EventType.Leave, EventType.PracticeClosed];
    const queryTypes: ['event.from' | 'event.to', ITimePeriod][] = [
      [
        'event.from',
        { from: range.from.clone().subtract(1, 'month'), to: range.to.clone() },
      ],
      [
        'event.to',
        {
          from: range.from.clone(),
          to: range.to.clone().add(3, 'month'),
        },
      ],
    ];
    const multiDayEvents$ = combineLatest(
      flatten(
        multiDayEventTypes.map((multiDayEventType) =>
          queryTypes.map(([field, queryRange]) =>
            Brand.eventQueryV2$(
              brand,
              queryRange,
              field,
              participants,
              multiDayEventType,
              practiceRef
            )
          )
        )
      )
    ).pipe(reduce2DArray());

    return combineLatest([inRangeEvents$, multiDayEvents$]).pipe(
      reduce2DArray(),
      map((events) => uniqWith(events, isSameRef)),
      multiFilter((event) =>
        timePeriodsIntersect(
          range,
          toTimePeriod(event.event.from, event.event.to),
          false,
          'minutes'
        )
      )
    );
  }

  // TODO: We should use aggregates for the participants
  static getGapCandidates$(
    brand: IReffable<IBrand>,
    range: ITimePeriod,
    practiceRefs: DocumentReference<IPractice>[] = [],
    participants: IParticipant[] = []
  ): Observable<WithRef<ICandidateCalendarEvent>[]> {
    const participantRefs = participants.map((participant) => participant.ref);

    if (practiceRefs.length) {
      return safeCombineLatest(
        practiceRefs.map((practiceRef) =>
          Brand.eventQueryV2$(
            brand,
            range,
            'event.from',
            participantRefs,
            EventType.GapCandidate,
            practiceRef
          )
        )
      ).pipe(
        reduce2DArray(),
        map((events) => events as WithRef<ICandidateCalendarEvent>[])
      );
    }

    return Brand.eventQueryV2$(
      brand,
      range,
      'event.from',
      participantRefs,
      EventType.GapCandidate
    ).pipe(map((events) => events as WithRef<ICandidateCalendarEvent>[]));
  }

  static getAppointmentRequests$(
    brand: IReffable<IBrand>,
    practiceRef?: DocumentReference<IPractice>
  ): Observable<WithRef<IAppointmentRequest>[]> {
    if (!practiceRef) {
      return query$(
        undeletedQuery(Brand.calendarEventCol(brand)),
        where('event.type', '==', EventType.AppointmentRequest)
      ).pipe(multiMap((event) => event as WithRef<IAppointmentRequest>));
    }

    return query$(
      undeletedQuery(Brand.calendarEventCol(brand)),
      where('event.type', '==', EventType.AppointmentRequest),
      where('practice.ref', '==', practiceRef)
    ).pipe(multiMap((event) => event as WithRef<IAppointmentRequest>));
  }

  static eventQuery<T extends object>(
    query: Query<T> | CollectionReference<T>,
    range: ITimePeriod
  ): Query<T> {
    return toQuery(
      query,
      where('event.from', '>=', range.from.toDate()),
      where('event.from', '<=', range.to.toDate()),
      where('deleted', '==', false)
    );
  }

  static eventQueryV2<T extends object>(
    existingQuery: Query<T> | CollectionReference<T>,
    range: ITimePeriod,
    field: 'event.from' | 'event.to',
    participants?: DocumentReference<IStaffer | IPatient>[],
    type?: EventType,
    practiceRef?: DocumentReference<IPractice>
  ): Query<T> {
    let builtQuery = toQuery(
      existingQuery,
      where(field, '>=', range.from.toDate()),
      where(field, '<=', range.to.toDate()),
      where('deleted', '==', false)
    );

    if (participants && participants.length) {
      builtQuery = toQuery(
        builtQuery,
        where(
          'event.participantRefs',
          'array-contains-any', // Hard limit of 10
          participants
        )
      );
    }

    if (practiceRef) {
      builtQuery = toQuery(
        builtQuery,
        where('event.practice.ref', '==', practiceRef)
      );
    }

    if (type) {
      builtQuery = toQuery(builtQuery, where('event.type', '==', type));
    }
    return builtQuery;
  }

  static eventQueryV2$(
    brand: IReffable<IBrand>,
    range: ITimePeriod,
    field: 'event.from' | 'event.to',
    participants?: DocumentReference<IStaffer | IPatient>[],
    type?: EventType,
    practiceRef?: DocumentReference<IPractice>
  ): Observable<WithRef<ICalendarEvent>[]> {
    if (!participants || participants.length < 10) {
      return query$(
        Brand.eventQueryV2(
          Brand.calendarEventCol(brand),
          range,
          field,
          participants,
          type,
          practiceRef
        )
      );
    }

    const participantGroups = chunk(participants, 10);
    return safeCombineLatest(
      participantGroups.map((group) =>
        query$(
          Brand.eventQueryV2(
            Brand.calendarEventCol(brand),
            range,
            field,
            group,
            type,
            practiceRef
          )
        )
      )
    ).pipe(map(reduceToSingleArray));
  }

  static eventPracticeQuery<T extends object>(
    query: Query<T>,
    practiceRefs: DocumentReference[]
  ): Query<T> {
    return toQuery(query, where('event.practice.ref', 'in', practiceRefs));
  }

  static organisationRef(
    brand: IReffable<IBrand>
  ): DocumentReference<IOrganisation> {
    return Firestore.getParentDocRef<IOrganisation>(brand.ref);
  }

  static organisation$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<IOrganisation>> {
    return Firestore.doc$(this.organisationRef(brand));
  }

  static findCalendarEventsIntersectingRange$(
    brand: WithRef<IBrand>,
    range: ITimePeriod
  ): Observable<WithRef<ICalendarEvent>[]> {
    const eventCol = Brand.calendarEventCol(brand);
    const toWithinRange$ = query$(
      eventCol,
      orderBy('event.to'),
      where('event.to', '>=', range.from.toDate()),
      where('event.to', '<=', range.to.toDate())
    );
    const fromWithinRange$ = query$(
      eventCol,
      orderBy('event.from'),
      where('event.from', '>=', range.from.toDate()),
      where('event.from', '<=', range.to.toDate())
    );
    return combineLatest([toWithinRange$, fromWithinRange$]).pipe(
      reduce2DArray()
    );
  }

  static smsConversationCol(
    brand: IReffable<IBrand>
  ): CollectionReference<SMSConversation> {
    return subCollection<SMSConversation>(
      brand.ref,
      BrandCollection.SMSConversations
    );
  }

  static smsConversationByPractice$(
    brand: IReffable<IBrand>,
    practice: IReffable<IPractice>,
    status: ConversationStatus
  ): Observable<WithRef<SMSConversation>[]> {
    return query$(
      undeletedQuery(Brand.smsConversationCol(brand)),
      where('practiceRef', '==', practice.ref),
      where('status', '==', status)
    );
  }

  static chatCol(brand: IReffable<IBrand>): CollectionReference<IChat> {
    return subCollection<IChat>(brand.ref, BrandCollection.Chats);
  }

  static smsMessageCol(
    brand: IReffable<IBrand>
  ): CollectionReference<SMSMessage> {
    return subCollection<SMSMessage>(brand.ref, BrandCollection.SMSMessages);
  }

  static smsMessageByPatient$(
    brand: IReffable<IBrand>,
    patient: IReffable<IPatient>
  ): Observable<WithRef<SMSMessage>[]> {
    return query$(
      undeletedQuery(Brand.smsMessageCol(brand)),
      where('patientRef', '==', patient.ref)
    );
  }

  static emailCol(brand: IReffable<IBrand>): CollectionReference<EmailMessage> {
    return subCollection<EmailMessage>(brand.ref, BrandCollection.Emails);
  }

  static emailByRecipient$(
    brand: IReffable<IBrand>,
    recipient: IReffable<IPatient | IContact>
  ): Observable<WithRef<EmailMessage>[]> {
    return Firestore.query$(
      undeletedQuery(Brand.emailCol(brand)),
      where('recipientRef', '==', recipient.ref)
    );
  }

  static emailByAuthor$(
    brand: IReffable<IBrand>,
    author: IReffable<IPatient | IContact>
  ): Observable<WithRef<EmailMessage>[]> {
    return Firestore.query$(
      Brand.emailCol(brand),
      where('authorRef', '==', author.ref)
    ).pipe(multiUndeleted());
  }

  static patientPhoneNumberCol(
    brand: IReffable<IBrand>
  ): CollectionReference<IPatientPhoneNumber> {
    return subCollection<IPatientPhoneNumber>(
      brand.ref,
      BrandCollection.PatientPhoneNumbers
    );
  }

  static reportingJobCol(
    brand: IReffable<IBrand>
  ): CollectionReference<ReportingJob> {
    return subCollection<ReportingJob>(
      brand.ref,
      BrandCollection.ReportingJobs
    );
  }

  static reportingJobs$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<ReportingJob>[]> {
    return all$(undeletedQuery(Brand.reportingJobCol(brand)));
  }

  static patientMetadataConfigurationCol(
    brand: IReffable<IBrand>
  ): CollectionReference<IPatientMetadataConfiguration> {
    return subCollection<IPatientMetadataConfiguration>(
      brand.ref,
      BrandCollection.PatientMetadataConfigurations
    );
  }

  static patientMetadataConfigurations$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<IPatientMetadataConfiguration>[]> {
    return query$(undeletedQuery(Brand.patientMetadataConfigurationCol(brand)));
  }

  static patientMetadataDisplayCol(
    brand: IReffable<IBrand>
  ): CollectionReference<IPatientMetadataDisplay> {
    return subCollection<IPatientMetadataDisplay>(
      brand.ref,
      BrandCollection.PatientMetadataDisplays
    );
  }

  static patientMetadataDisplays$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<IPatientMetadataDisplay>[]> {
    return query$(undeletedQuery(Brand.patientMetadataDisplayCol(brand)));
  }

  static logoStoragePath(brand: IReffable<IBrand>): string {
    return `${brand.ref.path}/logo`;
  }

  static emailAttachmentStoragePath(brand: IReffable<IBrand>): string {
    return `${brand.ref.path}/email-attachments`;
  }

  static emailAttachmentMediaCol(
    brand: IReffable<IBrand>
  ): CollectionReference<IMedia> {
    return subCollection<IMedia>(
      brand.ref,
      BrandCollection.EmailAttachmentMedia
    );
  }
}

export const BRAND_NAMES: string[] = ['Dental Collective'];

export const BRANDS: IBrand[] = BRAND_NAMES.map((name: string) =>
  Brand.init({ name })
);
